import React from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, SlidersHorizontal } from "lucide-react";
import { specializations, experienceLevels, personTypes } from "../../data/experts";

function FilterContent({ filters, setFilters, onReset, onClose, showHeader }) {
  return (
    <>
      {showHeader && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-primary-400" />
            <h3 className="text-xl font-light text-neutral-200">Фильтры</h3>
          </div>
          <button onClick={onClose} className="p-2 text-neutral-400">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      <div className="space-y-6">
        <div>
          <h4 className="text-lg font-medium text-neutral-300 mb-3">Тип участника</h4>
          <div className="space-y-2">
            {personTypes.map((pt) => (
              <div
                key={pt.id}
                className="flex items-center gap-3 cursor-pointer group"
                onClick={() =>
                  setFilters({ ...filters, personType: pt.id })
                }
              >
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    filters.personType === pt.id
                      ? "bg-primary-700 border-primary-700"
                      : "border-neutral-600 group-hover:border-neutral-400"
                  }`}
                >
                  {filters.personType === pt.id && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="text-lg font-extralight text-neutral-400 group-hover:text-neutral-200">
                  {pt.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-lg font-medium text-neutral-300 mb-3">Специализация</h4>
          <div className="space-y-2">
            {specializations.map((spec) => (
              <div
                key={spec}
                className="flex items-center gap-3 cursor-pointer group"
                onClick={() => {
                  const newSpecs = filters.specializations.includes(spec)
                    ? filters.specializations.filter((s) => s !== spec)
                    : [...filters.specializations, spec];
                  setFilters({ ...filters, specializations: newSpecs });
                }}
              >
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    filters.specializations.includes(spec)
                      ? "bg-primary-700 border-primary-700"
                      : "border-neutral-600 group-hover:border-neutral-400"
                  }`}
                >
                  {filters.specializations.includes(spec) && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="text-lg font-extralight text-neutral-400 group-hover:text-neutral-200">
                  {spec}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-lg font-medium text-neutral-300 mb-3">Опыт</h4>
          <div className="space-y-2">
            {experienceLevels.map((level) => (
              <div
                key={level.label}
                className="flex items-center gap-3 cursor-pointer group"
                onClick={() => {
                  const newLevels = filters.experienceLevels.includes(level.label)
                    ? filters.experienceLevels.filter((l) => l !== level.label)
                    : [...filters.experienceLevels, level.label];
                  setFilters({ ...filters, experienceLevels: newLevels });
                }}
              >
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    filters.experienceLevels.includes(level.label)
                      ? "bg-primary-700 border-primary-700"
                      : "border-neutral-600 group-hover:border-neutral-400"
                  }`}
                >
                  {filters.experienceLevels.includes(level.label) && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="text-lg font-extralight text-neutral-400 group-hover:text-neutral-200">
                  {level.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-lg font-medium text-neutral-300 mb-3">Доступность</h4>
          <div
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() =>
              setFilters({ ...filters, availableOnly: !filters.availableOnly })
            }
          >
            <div
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                filters.availableOnly
                  ? "bg-primary-700 border-primary-700"
                  : "border-neutral-600 group-hover:border-neutral-400"
              }`}
            >
              {filters.availableOnly && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <span className="text-lg font-extralight text-neutral-400 group-hover:text-neutral-200">
              Только доступные
            </span>
          </div>
        </div>

        <div>
          <h4 className="text-lg font-medium text-neutral-300 mb-3">Стоимость (руб/час)</h4>
          <div className="flex items-center gap-3">
            <input
              type="number"
              placeholder="От"
              value={filters.priceMin || ""}
              onChange={(e) => setFilters({ ...filters, priceMin: e.target.value })}
              className="w-full px-3 py-2 rounded-md bg-neutral-800/50 border border-neutral-700/50 text-neutral-200 text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-700"
            />
            <span className="text-neutral-600">—</span>
            <input
              type="number"
              placeholder="До"
              value={filters.priceMax || ""}
              onChange={(e) => setFilters({ ...filters, priceMax: e.target.value })}
              className="w-full px-3 py-2 rounded-md bg-neutral-800/50 border border-neutral-700/50 text-neutral-200 text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-700"
            />
          </div>
        </div>

        <button
          onClick={onReset}
          className="w-full py-3 rounded-md border border-neutral-700/50 text-neutral-400 hover:text-neutral-200 hover:border-neutral-600 text-lg font-extralight transition-colors"
        >
          Сбросить фильтры
        </button>
      </div>
    </>
  );
}

export default function FilterSidebar({
  isOpen,
  onClose,
  filters,
  setFilters,
  onReset,
  isDesktop
}) {
  if (isDesktop) {
    return (
      <aside id="filter-sidebar_desktop">
        <FilterContent
          filters={filters}
          setFilters={setFilters}
          onReset={onReset}
          onClose={onClose}
          showHeader={false}
        />
      </aside>
    );
  }

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.aside
            id="filter-sidebar_mobile"
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed left-0 top-0 h-full w-80 bg-neutral-900 border-r border-neutral-800/30 z-50 overflow-y-auto lg:hidden"
          >
            <div className="p-6">
              <FilterContent
                filters={filters}
                setFilters={setFilters}
                onReset={onReset}
                onClose={onClose}
                showHeader={true}
              />
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
